import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import type {
  ReminderProfile,
  ReminderProfileEntry,
} from '@/types/settings-api';

/**
 * Practice Reminder Profiles — PROD-790 follow-up.
 *
 *   GET  /api/Practice/ReminderList/{practiceId}
 *   POST /api/Practice/CreateReminderProfile?PracticeId={id}
 *
 * The POST is a **full-array upsert** with per-row dirty-flag gating.
 * Mirrors the `isUpdated` pattern from Staff Professional Details
 * (PROD-788) but at the profile level — backend writes only profiles
 * with `isUpdated: true`. New profiles ship with `id: null` (the null
 * id is the insert signal regardless of `isUpdated`).
 *
 * **Note `PracticeId` query param has capital P** — verbatim per
 * captured curl. The `apiClient` URL builder preserves it.
 *
 * **Note POST body is a JSON ARRAY at the root**, not a wrapper object.
 *
 * See `buildReminderProfilesSaveBody` for the per-row diff logic.
 */

export const reminderProfilesQueryKey = (
  practiceId: string | null | undefined,
) => ['settings', 'reminder-profiles', practiceId] as const;

export function useReminderProfiles(
  practiceId: string | null | undefined,
) {
  return useQuery({
    queryKey: reminderProfilesQueryKey(practiceId),
    queryFn: ({ signal }) => {
      if (!practiceId) {
        throw new Error('useReminderProfiles called without practiceId');
      }
      return get<ReminderProfile[]>(
        `/api/Practice/ReminderList/${practiceId}`,
        { signal },
      );
    },
    enabled: !!practiceId,
  });
}

/**
 * POST the full reminder-profile array. **Caller assembles the body via
 * `buildReminderProfilesSaveBody`; this hook is pure transport.**
 *
 * Cache invalidation auto-fires on success against the GET key so the
 * form re-fetches the server-canonical state (with freshly-allocated
 * ids on new profiles + `isUpdated` cleared back to false).
 */
export function useSaveReminderProfiles(
  practiceId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReminderProfile[]) => {
      if (!practiceId) {
        throw new Error(
          'useSaveReminderProfiles called without practiceId',
        );
      }
      const qs = new URLSearchParams({ PracticeId: practiceId });
      return post<unknown>(
        `/api/Practice/CreateReminderProfile?${qs.toString()}`,
        body,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: reminderProfilesQueryKey(practiceId),
      });
    },
  });
}

// === Save-body mapper ===

export interface BuildReminderProfilesSaveBodyInput {
  /** Original GET response (snapshot before edits). */
  source: ReminderProfile[];
  /** Edited array (current React draft). */
  draft: ReminderProfile[];
}

/**
 * Compose the POST body from the original GET + the current React draft.
 *
 * Behaviour per the captured dev cURLs:
 *
 * 1. **All draft profiles ship in the array** (full-state semantics —
 *    omitted profiles get deleted by the backend's array-replace).
 * 2. **`isUpdated: true` on touched rows**, `false` on untouched.
 *    Backend writes only touched rows.
 * 3. **New profiles** (id absent in source): ship with `id: null`,
 *    `isUpdated: false`. The null id is the insert signal.
 * 4. **`practitionerId: ""` (empty string) on the POST body**, even
 *    though the GET returns `null` — asymmetric wire convention.
 * 5. **`reminderProfileType[].reminderId` is OMITTED** on save — the
 *    backend infers from the enclosing profile.
 * 6. **Type / whenToSend / number values preserved verbatim** — DO NOT
 *    normalise the `"InApp"` / `"In-App"` casing drift or the trailing
 *    space on `"After "`. Legacy data sanity depends on the round-trip
 *    being a no-op when nothing was touched.
 *
 * To DELETE a profile: omit it from the draft array. The backend's
 * array-replace removes it server-side.
 */
export function buildReminderProfilesSaveBody(
  input: BuildReminderProfilesSaveBodyInput,
): ReminderProfile[] {
  const { source, draft } = input;
  const sourceById = new Map(
    source.filter((p): p is ReminderProfile & { id: string } => !!p.id).map((p) => [p.id, p]),
  );

  return draft.map((row) => {
    const original = row.id ? sourceById.get(row.id) : undefined;
    const isUpdated = row.id !== null && !!original && !isProfileEqual(original, row);
    return {
      id: row.id,
      organizationId: row.organizationId,
      practiceId: row.practiceId,
      // GET null → POST "" (asymmetric wire convention)
      practitionerId: '',
      profileName: row.profileName,
      customNote: row.customNote,
      enableInAppBooking: row.enableInAppBooking,
      isDefault: row.isDefault,
      isUpdated,
      reminderProfileType: row.reminderProfileType.map((entry) =>
        stripReminderId(entry),
      ),
    };
  });
}

/** Deep equality for a profile — used to set the `isUpdated` flag. */
function isProfileEqual(a: ReminderProfile, b: ReminderProfile): boolean {
  if (
    a.profileName !== b.profileName ||
    a.customNote !== b.customNote ||
    a.enableInAppBooking !== b.enableInAppBooking ||
    a.isDefault !== b.isDefault
  ) {
    return false;
  }
  if (a.reminderProfileType.length !== b.reminderProfileType.length) return false;
  return a.reminderProfileType.every((entry, i) => {
    const other = b.reminderProfileType[i];
    return (
      entry.id === other.id &&
      entry.type === other.type &&
      entry.time === other.time &&
      entry.whenToSend === other.whenToSend &&
      entry.number === other.number
    );
  });
}

/**
 * Strip the parent-backref on POST. The GET ships `reminderId` on each
 * trigger row; the POST body omits it (backend infers from the
 * enclosing profile context).
 */
function stripReminderId(
  entry: ReminderProfileEntry,
): Omit<ReminderProfileEntry, 'reminderId'> {
  const { reminderId: _omit, ...rest } = entry;
  return rest;
}
